import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useEffect, useRef } from 'react';

import { Site } from '~hooks/useSites/models';

import { SiteCard } from './SiteCard';

interface SiteCardListProps {
  sites: Site[];
  selectedSiteId: string | null;
  onSiteSelect: (siteId: string) => void;
  onSiteEdit: (siteId: string) => void;
  isLoading?: boolean;
}

export function SiteCardList({
  sites,
  selectedSiteId,
  onSiteSelect,
  onSiteEdit,
  isLoading,
}: SiteCardListProps) {
  const theme = useTheme();
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const setCardRef = useCallback((siteId: string, element: HTMLDivElement | null) => {
    if (element) {
      cardRefs.current.set(siteId, element);
    } else {
      cardRefs.current.delete(siteId);
    }
  }, []);

  // Auto-scroll to selected card when selection changes from map pin click
  useEffect(() => {
    if (selectedSiteId) {
      const cardElement = cardRefs.current.get(selectedSiteId);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedSiteId]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          bgcolor: theme.brandV2.colors.treadGray10,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (sites.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          bgcolor: theme.brandV2.colors.treadGray10,
          p: 2,
        }}
      >
        <Typography sx={{ color: theme.brandV2.colors.treadGray4, textAlign: 'center' }}>
          {t('administration.site.no_sites_found')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1,
        overflow: 'auto',
        height: '100%',
        bgcolor: theme.brandV2.colors.treadGray10,
      }}
    >
      {sites.map((site) => (
        <SiteCard
          key={site.id}
          ref={(el) => setCardRef(site.id, el)}
          site={site}
          isSelected={site.id === selectedSiteId}
          onClick={onSiteSelect}
          onEditClick={onSiteEdit}
        />
      ))}
    </Box>
  );
}
